class HistoryTimeline extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.history-timeline__viewport');
    this.track = this.querySelector('.history-timeline__track');
    this.barsWrap = this.querySelector('.history-timeline__bars');

    if (!this.track) return;

    this.initMilestoneRefs();
    if (this.milestoneElements.length === 0) {
      if (this.barsWrap) this.barsWrap.hidden = true;
      return;
    }

    this._onScroll = this.onScroll.bind(this);
    this._onResize = this.debounce(this.onResize.bind(this), 150);
    this._mq = window.matchMedia('(min-width: 990px)');
    this._onMqChange = () => this.rebuild();

    this.track.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);
    if (this._mq.addEventListener) {
      this._mq.addEventListener('change', this._onMqChange);
    } else if (this._mq.addListener) {
      this._mq.addListener(this._onMqChange);
    }

    this.rebuild();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    if (this._mq) {
      if (this._mq.removeEventListener) {
        this._mq.removeEventListener('change', this._onMqChange);
      } else if (this._mq.removeListener) {
        this._mq.removeListener(this._onMqChange);
      }
    }
  }

  initMilestoneRefs() {
    const direct = [...this.track.querySelectorAll(':scope > .history-timeline__slide')];
    if (direct.length) {
      this.milestoneElements = direct;
    } else {
      this.milestoneElements = [...this.track.querySelectorAll('.history-timeline__slide')];
    }
  }

  getPerRow() {
    const d = Math.max(1, parseInt(this.dataset.perRowDesktop || '3', 10));
    const m = Math.max(1, parseInt(this.dataset.perRowMobile || '2', 10));
    return this._mq && this._mq.matches ? d : m;
  }

  updateCssVars(perRow) {
    const mobileGap = `${parseInt(this.dataset.mobileGap || '28', 10)}px`;
    this.style.setProperty('--ht-active-cols', String(perRow));
    this.style.setProperty('--ht-active-gap', this._mq.matches ? '2.5rem' : mobileGap);
  }

  rebuild() {
    if (!this.milestoneElements || this.milestoneElements.length === 0) return;

    const perRow = this.getPerRow();
    const milestones = this.milestoneElements;
    const n = milestones.length;

    this.updateCssVars(perRow);

    this.track.innerHTML = '';
    for (let i = 0; i < n; i += perRow) {
      const page = document.createElement('li');
      page.className = 'history-timeline__page';
      const row = document.createElement('ul');
      row.className = 'history-timeline__row';
      row.setAttribute('role', 'list');
      const end = Math.min(i + perRow, n);
      for (let j = i; j < end; j++) {
        row.appendChild(milestones[j]);
      }
      page.appendChild(row);
      this.track.appendChild(page);
    }

    const pageCount = Math.ceil(n / perRow);
    this.track.classList.toggle('history-timeline__track--paged', pageCount > 1);
    this.track.scrollLeft = 0;

    this.pageElements = [...this.track.querySelectorAll('.history-timeline__page')];
    this.rebuildBars(pageCount);
    this.syncBars();
    this.updateBarsVisibility();
  }

  rebuildBars(pageCount) {
    if (!this.barsWrap) return;
    this.barsWrap.innerHTML = '';
    this.bars = [];

    if (pageCount < 2) {
      this.barsWrap.hidden = true;
      this.barsWrap.setAttribute('aria-hidden', 'true');
      return;
    }

    this.barsWrap.hidden = false;
    this.barsWrap.setAttribute('aria-hidden', 'false');

    for (let i = 0; i < pageCount; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'history-timeline__bar' + (i === 0 ? ' is-active' : '');
      btn.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + pageCount);
      btn.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      const idx = i;
      btn.addEventListener('click', () => this.onBarClick(idx));
      this.barsWrap.appendChild(btn);
      this.bars.push(btn);
    }
  }

  updateBarsVisibility() {
    if (!this.barsWrap) return;
    const show = this.pageElements && this.pageElements.length > 1;
    this.barsWrap.hidden = !show;
    this.barsWrap.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  onBarClick(index) {
    const slide = this.pageElements[index];
    if (!slide) return;
    slide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  }

  onScroll() {
    this.syncBars();
  }

  onResize() {
    this.syncBars();
  }

  syncBars() {
    if (!this.pageElements || this.pageElements.length < 2 || !this.bars || this.bars.length === 0)
      return;

    const step =
      this.pageElements.length > 1
        ? this.pageElements[1].offsetLeft - this.pageElements[0].offsetLeft
        : 0;
    const raw = step > 0 ? Math.round(this.track.scrollLeft / step) : 0;
    const active = Math.min(Math.max(0, raw), this.pageElements.length - 1);

    this.bars.forEach((bar, i) => {
      const on = i === active;
      bar.classList.toggle('is-active', on);
      bar.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }

  debounce(fn, ms) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }
}

customElements.define('history-timeline', HistoryTimeline);
